import { Chart as ChartJS } from 'chart.js/auto';
import { Chart } from 'primereact/chart';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import LoadingSpinner from '../../component/loadingoverlay/LoadingSpinner';
import PeriodSelector from '../../component/periodselector/PeriodSelector';
import { TransactionService } from '../../service/TransactionService';
import { LocationService } from '../../service/LocationService';
import { FormattedUtils } from '../../utils/FormattedUtils';
import './FinancialReportWidget.scss';

import { ReactComponent as CardIcon } from '../../assets/images/financial_report_card_icon.svg';
import { ReactComponent as CashIcon } from '../../assets/images/financial_report_cash_icon.svg';
import { ReactComponent as OtherIcon } from '../../assets/images/financial_report_other_icon.svg';
import { ReactComponent as TotalIcon } from '../../assets/images/financial_report_total_icon.svg';
import { ReactComponent as ProgressUpIcon } from '../../assets/images/progress-up.svg';
import { ReactComponent as ProgressDownIcon } from '../../assets/images/progress-down.svg';
import { ReactComponent as EyeIcon } from '../../assets/images/eye.svg';
import { ReactComponent as EyeSlashIcon } from '../../assets/images/eye-slash.svg';
import { ReactComponent as FinancialReportIcon } from '../../assets/images/financial_report_icon.svg';
import { MessageContext } from '../../context/MessageContext';
import Dropdown from '../../component/input/Dropdown';
import moment from 'moment';



function startOfDay(date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
}

function startOfWeekMonday(date) {
    const d = startOfDay(date);
    const day = d.getDay(); // 0=Sun, 1=Mon
    const diff = (day === 0 ? -6 : 1 - day);
    d.setDate(d.getDate() + diff);
    return d;
}

function dayKey(date) {
    return startOfDay(date).toISOString();
}

function weekKey(date) {
    return startOfWeekMonday(date).toISOString();
}

function hourKey(date) {
    const d = new Date(date);
    d.setMinutes(0, 0, 0);
    return d.toISOString();
}

function threeHourKey(date) {
    const d = new Date(date);
    const h = d.getHours();
    d.setHours(h - (h % 3), 0, 0, 0);
    return d.toISOString();
}

function monthKey(date) {
    const d = new Date(date);
    d.setDate(1); // Pierwszy dzień miesiąca
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
}

function fifteenMinKey(date) {
    const d = new Date(date);
    const minutes = d.getMinutes();
    d.setMinutes(minutes - (minutes % 15), 0, 0);
    return d.toISOString();
}

function aggregate(points, bin) {
    const map = new Map();
    const keyFn = bin === '15min' ? fifteenMinKey :
        bin === 'hour' ? hourKey :
            bin === '3h' ? threeHourKey :
                bin === 'week' ? weekKey :
                    bin === 'month' ? monthKey :
                        dayKey;
    points.forEach(p => {
        const key = keyFn(p.date); // Changed from p.timestamp to p.date
        const acc = map.get(key) || { card: 0, cash: 0, other: 0, date: new Date(key) };
        acc.card += p.card;
        acc.cash += p.cash;
        acc.other += p.other;
        map.set(key, acc);
    });
    return Array.from(map.values()).sort((a, b) => a.date - b.date);
}

function formatLabels(items, bin, locale = 'pl-PL', t) {
    if (bin === '15min') {
        // Dla 15-minutowych punktów, pokaż etykiety co godzinę
        return items.map(i => {
            const minutes = i.date.getMinutes();
            return minutes === 0 ? i.date.toLocaleTimeString(locale, { hour: '2-digit' }) : '';
        });
    }
    if (bin === 'hour') return items.map(i => i.date.toLocaleTimeString(locale, { hour: '2-digit' }));
    if (bin === '3h') {
        // label tylko na północ – nazwa dnia
        return items.map(i => (i.date.getHours() === 0 ? i.date.toLocaleDateString(locale, { weekday: 'long' }) : ''));
    }
    if (bin === 'day') {
        // Sprawdź czy to okres tygodniowy (7 dni) - wtedy pokaż nazwy dni tygodnia
        // Sprawdzamy czy mamy 7 lub 8 dni (może API zwraca dodatkowy dzień)
        if (items.length === 7 || items.length === 8) {
            const weekdayNames = items.map(i => {
                const dayOfWeek = i.date.getDay();
                const weekdayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                return t ? t(`financialReportWidget.weekdays.${weekdayKeys[dayOfWeek]}`) : i.date.toLocaleDateString(locale, { weekday: 'long' });
            });
            return weekdayNames;
        }
        // Dla okresu 90 dni, pokaż etykiety co tydzień (niedziela)
        if (items.length > 60) {
            return items.map(i => {
                const dayOfWeek = i.date.getDay();
                return dayOfWeek === 0 ? i.date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' }) : '';
            });
        }
        // W przeciwnym razie pokaż datę
        return items.map(i => i.date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' }));
    }
    if (bin === 'week') return items.map(i => startOfWeekMonday(i.date).toLocaleDateString(locale, { day: '2-digit', month: '2-digit' }));
    if (bin === 'month') {
        // Dla miesięcznych okresów, pokaż etykiety co dzień
        return items.map(i => i.date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' }));
    }
    return items.map(() => '');
}


function daysBetween(dateA, dateB) {
    const a = new Date(Date.UTC(dateA.getFullYear(), dateA.getMonth(), dateA.getDate()));
    const b = new Date(Date.UTC(dateB.getFullYear(), dateB.getMonth(), dateB.getDate()));
    const MS_PER_DAY = 24 * 60 * 60 * 1000;
    return Math.round((b - a) / MS_PER_DAY); // lub Math.floor — patrz uwagi
}


function lineBinForRange(period) {
    if (!period || !period.from || !period.to) {
        return 'hour';
    }
    const days = daysBetween(period.from, period.to);
    if (days <= 1) {
        return 'hour';
    } else if (days <= 7) {
        return '3h';
    } else if (days <= 31) {
        return 'day';
    } else if (days <= 90) {
        return 'day'; // Punkty co dzień dla wykresu liniowego
    } else {
        return 'month';
    }
}

function barBinForRange(period) {
    if (!period || !period.from || !period.to) {
        return 'hour';
    }
    const days = daysBetween(period.from, period.to);
    if (days <= 1) {
        return 'hour';
    } else if (days <= 7) {
        return 'day';
    } else if (days <= 31) {
        return 'day';
    } else if (days <= 90) {
        return 'week';
    } else {
        return 'month';
    }
}

// Helper function to format date for tooltip
function formatDateForTooltip(date, bin, locale = 'pl-PL') {
    if (!date || isNaN(date.getTime())) {
        return 'Invalid date';
    }
    //console.log('date', moment(date).format('YYYY-MM-DD HH:mm:ss'))
    //console.log('bin', bin)
        
    // Użyj moment z lokalizacją aplikacji
    const m = moment(date).locale(i18n.language);
    if (bin === 'hour' || bin === '3h') {
        // For hourly data, show time range and date
        const hour = date.getHours();
        const nextHour = hour + (bin === '3h' ? 3 : 1);
        const timeRange = `${hour.toString().padStart(2, '0')}:00 - ${(nextHour - 1).toString().padStart(2, '0')}:59`;
        const dayName = m.format('dddd');
        const month = m.format('MMMM');
        const day = m.date();
        const year = m.year();
        return `${timeRange} / ${dayName} ${day} / ${month} ${year}`;
    } else if (bin === 'day') {
        const dayName = m.format('dddd');
        const month = m.format('MMMM');
        const day = m.date();
        const year = m.year();
        return `${dayName} ${day} / ${month} ${year}`;
    } else if (bin === 'week') {
        const from = m.clone().subtract(6, 'days').format('ddd DD');
        const to = m.format('ddd DD');
        return `${from} - ${to} / ${m.format('MMMM')} ${m.format('YYYY')}`;

    } else if (bin === 'month') {
        return `${m.format('MMMM')} ${m.format('YYYY')}`;
    } else {        
        return m.format('YYYY-MM-DD');
    }
}

// Plugin to draw dashed horizontal grid lines for Chart.js v4+
const dashedGridPlugin = {
    id: 'dashedGrid',
    afterDraw(chart, args, pluginOptions) {

        // Only draw grid lines for bar and line charts, not for doughnut
        if (chart.config.type === 'doughnut') {
            return;
        }

        const { ctx, chartArea, scales } = chart;
        if (!chartArea || !scales || !scales.y) {
            return;
        }

        const { left, right } = chartArea;
        const yScale = scales.y;

        const dash = (pluginOptions && pluginOptions.dash) || [1, 3];
        const lineWidth = (pluginOptions && pluginOptions.lineWidth) || 1;
        const color = (pluginOptions && pluginOptions.color) || 'rgba(0,0,0,0.1)';

        ctx.save();
        ctx.setLineDash(dash);
        ctx.lineWidth = lineWidth;
        ctx.strokeStyle = color;

        // Draw a dashed line for each tick
        (yScale.ticks || []).forEach((t, index) => {
            const y = yScale.getPixelForValue(t.value);
            ctx.beginPath();
            ctx.moveTo(left, y);
            ctx.lineTo(right, y);
            ctx.stroke();
        });

        ctx.restore();
    }
};


function gradientBG(solid, stops) {
    return (ctx) => {
        const chart = ctx?.chart;
        // legenda / brak chartArea / nie bar -> stały kolor (spójny z line i legendą)
        if (!chart?.chartArea || chart.config.type !== 'bar') return solid;

        const { top, bottom } = chart.chartArea;
        const g = chart.ctx.createLinearGradient(0, top, 0, bottom);
        stops.forEach(([offset, color]) => g.addColorStop(offset, color));
        return g;
    };
}

// Plugin to fill white background on canvas
const whiteBackgroundPlugin = {
    id: 'whiteBackground',
    beforeDraw(chart, args, pluginOptions) {
        const { ctx } = chart;
        const canvas = ctx && ctx.canvas;
        if (!canvas) return;
        ctx.save();
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.restore();
    }
};


// Plugin to draw center text inside doughnut
const centerTextPlugin = {
    id: 'centerText',
    afterDraw(chart, args, pluginOptions) {
        if (chart.config.type !== 'doughnut') return;
        const { ctx, chartArea } = chart;
        if (!chartArea) return;
        const centerX = (chartArea.left + chartArea.right) / 2;
        const centerY = (chartArea.top + chartArea.bottom) / 2;
        const title = (pluginOptions && pluginOptions.title) || '';
        const subtitle = (pluginOptions && pluginOptions.subtitle) || '';
        const titleColor = (pluginOptions && pluginOptions.titleColor) || '#736f89';
        const subtitleColor = (pluginOptions && pluginOptions.subtitleColor) || '#1e1531';

        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // Title (small)
        ctx.fillStyle = titleColor;
        ctx.font = '500 12px Inter-Medium, sans-serif';
        ctx.fillText(title, centerX, centerY - 14);

        // Subtitle (big)
        ctx.fillStyle = subtitleColor;
        ctx.font = '700 22px Inter-Bold, sans-serif';
        ctx.fillText(subtitle, centerX, centerY + 10);

        ctx.restore();
    }
};

// Plugin to draw permanent labels on doughnut chart
const doughnutLabelsPlugin = {
    id: 'doughnutLabels',
    afterDraw(chart, args, pluginOptions) {
        if (chart.config.type !== 'doughnut') return;
        const { ctx, chartArea } = chart;
        if (!chartArea) return;

        const dataset = chart.data.datasets[0];
        const data = Array.isArray(dataset?.data) ? dataset.data : [];
        const labels = Array.isArray(chart.data.labels) ? chart.data.labels : [];
        
        // Użyj pełnej sumy ze wszystkich części (przekazanej przez pluginOptions)
        // Jeśli nie ma pełnej sumy, użyj sumy z wyświetlanych danych jako fallback
        const fullTotal = (pluginOptions && pluginOptions.fullTotal) || data.reduce((a, b) => a + b, 0);

        // Sprawdź czy to jest sytuacja gdy CARD i CASH są wyłączone (tylko TOTAL jako pełne koło)
        const isOnlyTotal = pluginOptions && pluginOptions.isOnlyTotal === true;

        // Nic nie rysuj, jeśli brak etykiet lub jest tylko TOTAL (CARD i CASH wyłączone)
        if (!labels.length || isOnlyTotal) {
            return;
        }

        const centerX = (chartArea.left + chartArea.right) / 2;
        const centerY = (chartArea.top + chartArea.bottom) / 2;
        const radius = Math.min(chartArea.width, chartArea.height) / 2;

        ctx.save();
        ctx.font = '500 12px Inter-Medium, sans-serif';

        // Position all labels to the right of the chart, vertically stacked
        const labelStartX = centerX + radius + 40; // Fixed position to the right
        const labelStartY = centerY - (data.length - 1) * 20; // Start from top, 40px spacing

        data.forEach((value, index) => {
            // Procent liczony względem pełnej sumy ze wszystkich części
            const safeTotal = fullTotal > 0 ? fullTotal : 0;
            const percentage = safeTotal > 0 ? ((value / safeTotal) * 100).toFixed(0) : '0';
            const label = labels[index] ?? '';
            const backgroundArray = Array.isArray(dataset.backgroundColor) ? dataset.backgroundColor : [];
            const backgroundColor = backgroundArray[index] || '#e0e0e0';
            
            // Calculate label position - all to the right, stacked vertically
            const labelX = labelStartX;
            const labelY = labelStartY + index * 40; // 40px spacing between labels
            
            // Draw colored dot
            ctx.fillStyle = backgroundColor;
            ctx.beginPath();
            ctx.arc(labelX, labelY, 4, 0, 2 * Math.PI);
            ctx.fill();
            
            // Draw label text in two lines with different styles
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
            
            // First line: Label name (uppercase, normal weight)
            ctx.fillStyle = '#736f89';
            ctx.font = '500 12px Inter-Medium, sans-serif';
            ctx.fillText(label?.toUpperCase(), labelX + 10, labelY - 8);
            
            // Second line: Percentage and value (bold)
            ctx.fillStyle = '#1e1531';
            ctx.font = '600 14px Inter-SemiBold, sans-serif';
            ctx.fillText(`${percentage}% / ${FormattedUtils.formatPrice(value || 0)} PLN`, labelX + 10, labelY + 8);
        });

        ctx.restore();
    }
};



// Chart Type Buttons Component
const ChartTypeButtons = ({ activeButton, onButtonClick, t }) => {
    const buttons = [
        { code: 'line', label: t('financialReportWidget.chartType.line') },
        { code: 'column', label: t('financialReportWidget.chartType.column') },
        { code: 'circle', label: t('financialReportWidget.chartType.circle') }
    ];

    return (
        <div className="chart-type-buttons">
            {buttons.map(button => (
                <button
                    key={button.code}
                    className={`chart-type-button ${activeButton === button.code ? 'active' : ''}`}
                    onClick={() => onButtonClick(button.code)}
                >
                    {button.label}
                </button>
            ))}
        </div>
    );
};

// Chart Legend Component
const ChartLegend = ({ datasets, disabledCards, t }) => {
    if (!datasets || datasets.length === 0) return null;

    return (
        <div className="chart-legend">
            {datasets.map((dataset, index) => {
                const isDisabled = disabledCards[dataset.variant] || false;
                const color = dataset.borderColor || dataset.backgroundColor;
                
                return (
                    <div 
                        key={index} 
                        className={`legend-item ${isDisabled ? 'disabled' : ''}`}
                    >
                        <div 
                            className="legend-color" 
                            style={{ backgroundColor: color }}
                        />
                        <span className="legend-label">{dataset.label}</span>
                    </div>
                );
            })}
        </div>
    );
};

const SummaryCard = ({ variant, value, currency = 'PLN', showProgress = false, isLoading = false, prevValue = 0, disabled = true, onClick = () => { } }) => {

    const { t } = useTranslation();

    const iconMap = {
        'card': <CardIcon />,
        'cash': <CashIcon />,
        'other': <OtherIcon />,
        'total': <TotalIcon />
    }

    const titleMap = {
        'card': t('financialReportWidget.paymentMethod.card'),
        'cash': t('financialReportWidget.paymentMethod.cash'),
        'other': t('financialReportWidget.paymentMethod.other'),
        'total': t('financialReportWidget.total')
    }

    const progressIconMap = {
        'up': <ProgressUpIcon />,
        'down': <ProgressDownIcon />
    }

    const progress = prevValue === 0 ? (value > 0 ? 100 : 0) : ((value - prevValue) / prevValue * 100);
    const formattedProgress = progress.toFixed(2);

    return (
        <div className={`financial-report-summary-card ${variant} ${disabled ? 'disabled' : ''}`}>
            <div className='state-switch' onClick={onClick}>
                {disabled ? <EyeIcon /> : <EyeSlashIcon />}
            </div>
            <div className='icon'>
                <div>
                    {iconMap[variant]}
                </div>
            </div>
            <div className='summary-card-content'>
                <div className='summary-card-content-title-wrapper'>
                    <div className='summary-card-content-title'>{titleMap[variant]}</div>
                    {showProgress && <div className='summary-card-content-progress-wrapper'>
                        {isLoading ? (
                            <div className='summary-card-content-progress-icon'><LoadingSpinner size='16px' color='var(--white)' /></div>
                        ) : (
                            <>
                                {progress !== 0 && <div className='summary-card-content-progress-icon'>{progressIconMap[progress > 0 ? 'up' : 'down']}</div>}
                                <div className='summary-card-content-progress-value'>{formattedProgress}%</div>
                            </>
                        )}
                    </div>}
                </div>
                <div className='summary-card-content-value-wrapper' style={{height: '54px'}}>
                    {!isLoading && (
                        <>
                            <span className='summary-card-content-value-value'>{FormattedUtils.formatPrice(value)}</span>
                            <span className='summary-card-content-value-currency'>{currency}</span>
                        </>
                    )}
                    
                </div>
            </div>
        </div>
    )
}

const FinancialReportWidget = () => {

    const [chartData, setChartData] = useState({});
    const [chartOptions, setChartOptions] = useState({});
    const [chartType, setChartType] = useState('bar');
    const [activeButton, setActiveButton] = useState('column'); // Active button
    const [period, setPeriod] = useState(null);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const { showErrorMessage } = useContext(MessageContext);
    const [summaryData, setSummaryData] = useState({
        totalCardSum: 0,
        totalCashSum: 0,
        totalOtherSum: 0,
        grandTotal: 0,
        prevCardSum: 0,
        prevCashSum: 0,
        prevOtherSum: 0,
        prevGrandTotal: 0
    });
    const [disabledCards, setDisabledCards] = useState({
        card: false,
        cash: false,
        other: false,
        total: false
    });
    const chartRef = useRef(null);
    const { t } = useTranslation();
    const [locationOptions, setLocationOptions] = useState([]);
    const [location, setLocation] = useState(null);
    const [isLoadingPrevRevenue, setIsLoadingPrevRevenue] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false);

    // Function to handle button clicks
    const handleButtonClick = useCallback((buttonCode) => {
        setActiveButton(buttonCode);

        const codeToType = {
            'line': 'line',
            'column': 'bar',
            'circle': 'doughnut'
        };

        const newType = codeToType[buttonCode];
        if (newType) {
            setChartType(newType);
        }
    }, []);

    // Function to handle card toggle
    const handleCardToggle = useCallback((cardType) => {
        setDisabledCards(prev => ({
            ...prev,
            [cardType]: !prev[cardType]
        }));
    }, []);




    // Add click event listener to canvas
    useEffect(() => {
        const handleCanvasClick = (event) => {
            const canvas = event.target;
            const rect = canvas.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;

            // Get chart instance from canvas
            const chart = ChartJS.getChart(canvas);
            if (!chart || !chart.buttonPositions) {
                return;
            }

            // Check if click is within any button
            chart.buttonPositions.forEach(button => {
                if (x >= button.x && x <= button.x + button.width &&
                    y >= button.y && y <= button.y + button.height) {
                    handleButtonClick(button.code);
                }
            });
        };

        // Wait for chart to be rendered, then add event listener
        const timer = setTimeout(() => {
            const canvas = chartRef.current?.canvas || document.querySelector('canvas');
            if (canvas) {
                canvas.addEventListener('click', handleCanvasClick);
                canvas.style.cursor = 'pointer';
            }
        }, 100);

        return () => {
            clearTimeout(timer);
            const canvas = chartRef.current?.canvas || document.querySelector('canvas');
            if (canvas) {
                canvas.removeEventListener('click', handleCanvasClick);
            }
        };
    }, [handleButtonClick, chartData]);

    useEffect(() => {
        ChartJS.register(dashedGridPlugin);
        ChartJS.register(whiteBackgroundPlugin);
        ChartJS.register(centerTextPlugin);
        ChartJS.register(doughnutLabelsPlugin);
        fetchLocationOptions();
        return () => {
            ChartJS.unregister(dashedGridPlugin);
            ChartJS.unregister(whiteBackgroundPlugin);
            ChartJS.unregister(centerTextPlugin);
            ChartJS.unregister(doughnutLabelsPlugin);
        };
    }, []);


    // const [barDates, setBarDates] = useState([]);
    // const [lineDates, setLineDates] = useState([]);

    const barDates = useRef([]);
    const lineDates = useRef([]);

    // Zwraca stały kolor dla legendy i dla line,
    // a gradient tylko gdy faktycznie rysujemy słupki (bar) i chartArea jest policzone.
    useEffect(() => {
        
        //const documentStyle = getComputedStyle(document.documentElement);
        // Get legend styles from CSS variables
        //const mainDarkPurple = documentStyle.getPropertyValue('--main-dark-purple') || '#1e1531';
        //const secondaryTitleFontFamily = documentStyle.getPropertyValue('--titles-secondary-title-font-family') || 'Inter-Medium, sans-serif';
        //const secondaryTitleFontSize = parseInt(documentStyle.getPropertyValue('--titles-secondary-title-font-size')) || 12;
        //const secondaryTitleFontWeight = parseInt(documentStyle.getPropertyValue('--titles-secondary-title-font-weight')) || 500;




        // POBIERZ DANE (prawdziwe dane z API)
        (async () => {
            setIsLoading(true);
            setError(null);

            try {
                const timeseries = await fetchFinancialReportData(period, location);
                fetchPrevRevenue(period, location);

                // SUMY do wykresu kołowego i kart podsumowujących
                const sum = (arr) => arr.reduce((acc, v) => acc + v, 0);
                const totalCardSum = sum(timeseries.map(p => p.card));
                const totalCashSum = sum(timeseries.map(p => p.cash));
                const totalOtherSum = sum(timeseries.map(p => p.other));
                const grandTotal = totalCardSum + totalCashSum + totalOtherSum;

                // Zapisz sumy w stanie dla kart podsumowujących
                setSummaryData(prev =>{
                    return {
                        ...prev,
                        totalCardSum,
                        totalCashSum,
                        totalOtherSum,
                        grandTotal
                    }
                });

                let labels;
                let cardValues;
                let cashValues;
                let otherValues;
                let totalValues;

                // Compute aggregated series based on selected range and chart type
                const lineBin = lineBinForRange(period);
                const barBin = barBinForRange(period);
                if (chartType === 'line') {
                    const agg = aggregate(timeseries, lineBin);
                    labels = formatLabels(agg, lineBin, i18n.language, t);
                    cardValues = agg.map(a => a.card);
                    cashValues = agg.map(a => a.cash);
                    otherValues = agg.map(a => a.other);
                    totalValues = agg.map(a => a.card + a.cash + a.other);
                    lineDates.current = agg.map(a => a.date);
                } else if (chartType === 'bar') {
                    const agg = aggregate(timeseries, barBin);
                    labels = formatLabels(agg, barBin, i18n.language, t);
                    cardValues = agg.map(a => a.card);
                    cashValues = agg.map(a => a.cash);
                    otherValues = agg.map(a => a.other);
                    totalValues = agg.map(a => a.card + a.cash + a.other);
                    barDates.current = agg.map(a => a.date);
                }

                let data;
                if (chartType === 'doughnut') {
                    // Build doughnut segments based on enabled payment types
                    const labels = [];
                    const values = [];
                    const colors = [];

                    if (!disabledCards.card) {
                        labels.push(t('financialReportWidget.paymentMethod.card'));
                        values.push(totalCardSum);
                        colors.push('#ff912b');
                    }
                    if (!disabledCards.cash) {
                        labels.push(t('financialReportWidget.paymentMethod.cash'));
                        values.push(totalCashSum);
                        colors.push('#37dbac');
                    }
                    // If in future 'other' is enabled, handle similarly
                    // if (!disabledCards.other) { ... }

                    // Jeśli CARD i CASH są wyłączone, pokaż TOTAL jako pełne niebieskie koło (jeśli TOTAL nie jest wyłączony)
                    if (disabledCards.card && disabledCards.cash && values.length === 0 && !disabledCards.total) {
                        labels.push(t('financialReportWidget.total'));
                        values.push(grandTotal);
                        colors.push('#2797D5'); // Niebieski kolor dla TOTAL
                    }

                    // Fallback to zero dataset to avoid Chart.js errors when all are disabled
                    const safeValues = values.length > 0 ? values : [0];
                    const safeColors = colors.length > 0 ? colors : ['#e0e0e0'];

                    data = {
                        labels,
                        datasets: [
                            {
                                data: safeValues,
                                backgroundColor: safeColors,
                                borderWidth: 0,
                                hoverOffset: 0,
                                borderRadius: 0
                            }
                        ]
                    };
                } else {
                    const datasets = [];

                    // Card dataset
                    if (!disabledCards.card) {
                        datasets.push({
                            label: t('financialReportWidget.paymentMethod.card'),
                            backgroundColor: gradientBG('#ff912b', [
                                [0, 'rgba(244,178,118,1)'],
                                [1, 'rgba(255,145,43,1)']
                            ]),
                            borderColor: '#ff912b',
                            borderWidth: chartType === 'line' ? 3 : 0,
                            borderRadius: 10,
                            fill: chartType === 'line' ? false : true,
                            tension: 0.4,
                            pointBackgroundColor: '#ff912b',
                            pointBorderColor: '#ffffff',
                            pointBorderWidth: 2,
                            pointRadius: chartType === 'line' ? 4 : 0,
                            data: cardValues
                        });
                    }

                    // Cash dataset
                    if (!disabledCards.cash) {
                        datasets.push({
                            label: t('financialReportWidget.paymentMethod.cash'),
                            backgroundColor: gradientBG('#37dbac', [
                                [0, 'rgba(55,219,172,1)'],
                                [1, 'rgba(9,177,130,1)']
                            ]),
                            borderColor: '#37dbac',
                            borderWidth: chartType === 'line' ? 3 : 0,
                            borderRadius: 10,
                            fill: chartType === 'line' ? false : true,
                            tension: 0.4,
                            pointBackgroundColor: '#37dbac',
                            pointBorderColor: '#ffffff',
                            pointBorderWidth: 2,
                            pointRadius: chartType === 'line' ? 4 : 0,
                            data: cashValues
                        });
                    }

                    // Other dataset
                    // if (!disabledCards.other) {
                    //     datasets.push({
                    //         label: t('financialReportWidget.paymentMethod.other'),
                    //         backgroundColor: gradientBG('#6C7BFF', [
                    //             [0, 'rgba(151,164,255,1)'],
                    //             [1, 'rgba(94,119,224,1)']
                    //         ]),
                    //         borderColor: '#6C7BFF',
                    //         borderWidth: chartType === 'line' ? 3 : 0,
                    //         borderRadius: 10,
                    //         fill: chartType === 'line' ? false : true,
                    //         tension: 0.4,
                    //         pointBackgroundColor: '#6C7BFF',
                    //         pointBorderColor: '#ffffff',
                    //         pointBorderWidth: 2,
                    //         pointRadius: chartType === 'line' ? 4 : 0,
                    //         data: otherValues
                    //     });
                    // }

                    // Total dataset
                    if (!disabledCards.total) {
                        datasets.push({
                            label: t('financialReportWidget.total'),
                            backgroundColor: gradientBG('#2797D5', [
                                [0, 'rgba(100,196,250,1)'],
                                [1, 'rgba(39,151,213,1)']
                            ]),
                            borderColor: '#2797D5',
                            borderWidth: chartType === 'line' ? 3 : 0,
                            borderRadius: 10,
                            fill: chartType === 'line' ? false : true,
                            tension: 0.4,
                            pointBackgroundColor: '#2797D5',
                            pointBorderColor: '#ffffff',
                            pointBorderWidth: 2,
                            pointRadius: chartType === 'line' ? 4 : 0,
                            data: totalValues
                        });
                    }

                    data = {
                        labels,
                        datasets
                    };
                }

                let options;
                const buttonsFromState = {
                    buttons: [
                        { code: 'line', label: t('financialReportWidget.chartType.line'), active: activeButton === 'line' },
                        { code: 'column', label: t('financialReportWidget.chartType.column'), active: activeButton === 'column' },
                        { code: 'circle', label: t('financialReportWidget.chartType.circle'), active: activeButton === 'circle' }
                    ]
                };

                



                if (chartType === 'doughnut') {
                    options = {
                        maintainAspectRatio: false,
                        aspectRatio: 0.8,
                        cutout: '68%',
                        layout: {
                            padding: {
                                top: 40
                            }
                        },
                        plugins: {
                            whiteBackground: {},
                            legend: {
                                display: false // Hide Chart.js legend, we'll use React component
                            },
                            centerText: {
                                title: disabledCards.total ? '' : t('financialReportWidget.total').toUpperCase(),
                                // Zawsze pokazuj pełną sumę ze wszystkich części, niezależnie od wyświetlania
                                subtitle: disabledCards.total
                                    ? ''
                                    : FormattedUtils.formatPrice(grandTotal) + ' PLN'
                            },
                            doughnutLabels: {
                                // Przekaż pełną sumę do pluginu, żeby procenty były liczone względem wszystkich części
                                fullTotal: grandTotal,
                                // Przekaż informację czy to jest tylko TOTAL (CARD i CASH wyłączone)
                                isOnlyTotal: disabledCards.card && disabledCards.cash && !disabledCards.total
                            },
                            tooltip: {
                                enabled: false
                            }
                        }
                    };
                } else {
                    // Determine the bin type for tooltip formatting
                    const bin = chartType === 'line' ? lineBinForRange(period) : barBinForRange(period);
                    
                    options = {
                        maintainAspectRatio: false,
                        aspectRatio: 0.8,
                        plugins: {
                            whiteBackground: {},
                            legend: {
                                display: false // Hide Chart.js legend, we'll use React component
                            },
                            dashedGrid: chartOptions.plugins?.dashedGrid || { dash: [1, 3], lineWidth: 1, color: '#e0e0e0' },
                            tooltip: {
                                enabled: true,
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                titleColor: '#1e1531',
                                bodyColor: '#1e1531',
                                borderColor: '#e0e0e0',
                                borderWidth: 1,
                                cornerRadius: 8,
                                displayColors: true,
                                usePointStyle: true,                                
                                boxWidth: 20,
                                boxHeight: 4,
                                callbacks: {
                                    title: function(context) {
                                        const dataIndex = context[0].dataIndex;
                                        
                                        let date;
                                        if (chartType === 'line') {
                                            date = lineDates.current[dataIndex];
                                        } else if (chartType === 'bar') {
                                            date = barDates.current[dataIndex];
                                        }
                                        return formatDateForTooltip(date, bin, i18n.language);
                                    },
                                    label: function(context) {
                                        const datasetLabel = context.dataset.label;
                                        const value = context.parsed.y;
                                        
                                        return `${datasetLabel}: ${FormattedUtils.formatPrice(value)}`;
                                    },
                                    afterBody: function(context) {
                                        return ''; // No additional content
                                    },
                                    labelPointStyle: function(context) {
                                        return {
                                            pointStyle: 'rectRounded',
                                            rotation: 0,                                                           // szerokość obramowania (dla kółek)
                                        };
                                    }
                                }
                            }
                        },
                        scales: {
                            x: {
                                ticks: {
                                    autoSkip: false,
                                    maxRotation: 0,
                                    minRotation: 0,
                                },
                                grid: { display: false, drawBorder: false }
                            },
                            y: {
                                grid: { display: false, drawBorder: false }
                            }
                        }
                    };
                }

                setChartData(data);
                setChartOptions(options);

            } catch (err) {
                console.error('FinancialReportWidget-fetchFinancialReportData-error', err);
                setError(t('financialReportWidget.error.fetchFinancialReportData'));
            } finally {
                setIsLoading(false);
            }
        })();
    }, [chartType, period, disabledCards, location, t]); // update when type, period, disabled cards or location change

    // Update chart options when activeButton changes
    useEffect(() => {
        setChartOptions(prevOptions => ({
            ...prevOptions,
            plugins: {
                ...prevOptions.plugins,
                customButtons: {
                    buttons: [
                        { code: 'line', label: t('financialReportWidget.chartType.line'), active: activeButton === 'line' },
                        { code: 'column', label: t('financialReportWidget.chartType.column'), active: activeButton === 'column' },
                        { code: 'circle', label: t('financialReportWidget.chartType.circle'), active: activeButton === 'circle' }
                    ]
                }
            }
        }));
    }, [activeButton]);

    const fetchFinancialReportData = async (period, locationId) => {
        if (period) {
            setIsLoadingData(true);
            try {
                return await TransactionService.getFinancialReport(period.from, period.to, locationId);
            } finally {
                setIsLoadingData(false);
            }
        } else {
            return [];
        }
    }

    const fetchPrevRevenue =  (period, locationId) => {
        if (period) {
            setIsLoadingPrevRevenue(true);
            TransactionService.getFinancialReportPrevRevenue(period.from, period.to, period.periodType, locationId)
            .then(response => {
                setSummaryData(prev => ({
                    ...prev,
                    prevCardSum: response.cardRevenue || 0,
                    prevCashSum: response.cashRevenue || 0,
                    prevOtherSum: response.otherRevenue || 0,
                    prevGrandTotal: (response.cardRevenue || 0) + (response.cashRevenue || 0) + (response.otherRevenue || 0)
                }));
            })
            .catch(error => {
                showErrorMessage(t('financialReportWidget.error.fetchPrevRevenue'));
            })
            .finally(() => {
                setIsLoadingPrevRevenue(false);
            });
        }
    }

    const fetchLocationOptions = async () => {
        try {
            const locations = await LocationService.getAllLocations();
            const formatLocation = (location) => {
                let result = location.name;
                if (location.city) {
                    result += ', ' + location.city;
                }
                return result;
            }
            setLocationOptions(locations.map(l => ({ id: l.id, fullName: formatLocation(l) })));
        } catch (err) {
            showErrorMessage(t('financialReportWidget.error.fetchLocationOptions'));
        }
    }

    return (
        <div className={`financial-report-widget widget data-card ${isCollapsed ? 'collapsed' : ''}`}>
            <div className="card">
                <div className='financial-report-widget-header'>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                        <div className='flex align-items-center gap-2'>
                            <FinancialReportIcon />
                            <span className='widget-name'>{t('financialReportWidget.widgetName')}</span>
                        </div>

                        <div className='right-container'>
                            <Dropdown className='w-18rem mb-0' small={true} id='period' options={locationOptions} value={location} onChange={e => setLocation(e.value)}                            
                                optionLabel="fullName" optionValue="id" filter placeholder={t('financialReportWidget.locationPlaceholder')}
                                showClear
                            />
                            <PeriodSelector
                                value={period}
                                onChange={(period) => {
                                    if (period && period.from && period.to) {
                                        setPeriod(period);
                                    }
                                }}
                                defaultFilterType="currentWeek"
                            />
                        </div>
                    </div>
                </div>
                <div className='flex justify-between gap-2 mb-3'>
                    <SummaryCard
                        variant='card'
                        value={summaryData.totalCardSum}
                        disabled={disabledCards.card}
                        onClick={() => handleCardToggle('card')}
                        showProgress={true}
                        prevValue={summaryData.prevCardSum}
                        isLoading={isLoadingPrevRevenue || isLoadingData}
                    />
                    <SummaryCard
                        variant='cash'
                        value={summaryData.totalCashSum}
                        disabled={disabledCards.cash}
                        onClick={() => handleCardToggle('cash')}
                        showProgress={true}
                        prevValue={summaryData.prevCashSum}
                        isLoading={isLoadingPrevRevenue || isLoadingData}
                    />
                    {/* <SummaryCard
                        variant='other'
                        value={summaryData.totalOtherSum}
                        disabled={disabledCards.other}
                        onClick={() => handleCardToggle('other')}
                    /> */}
                    <SummaryCard
                        variant='total'
                        value={summaryData.grandTotal}
                        disabled={disabledCards.total}
                        onClick={() => handleCardToggle('total')}
                        showProgress={true}
                        prevValue={summaryData.prevGrandTotal}
                        isLoading={isLoadingPrevRevenue || isLoadingData}
                    />
                </div>
                {/* Chart Controls */}
                <div className="separator" />
                <div className="chart-controls">
                    <ChartTypeButtons 
                        activeButton={activeButton}
                        onButtonClick={handleButtonClick}
                        t={t}
                    />
                    {chartType !== 'doughnut' && (
                        <ChartLegend 
                            datasets={chartData.datasets || []}
                            disabledCards={disabledCards}
                            t={t}
                        />
                    )}
                    <div className="chart-controls-spacer"></div>
                </div>

                {isLoading ? (
                    <div className="chart-container-loading">
                        <div><LoadingSpinner /></div>
                    </div>
                ) : error ? (
                    <div className="chart-container-error">
                        <div>{error}</div>
                    </div>
                ) : (
                    <Chart
                        className="chart-container"
                        ref={chartRef}
                        type={chartType}
                        data={chartData}
                        options={chartOptions}
                        plugins={[dashedGridPlugin, doughnutLabelsPlugin]}
                    />
                )}
            </div>
        </div>
    )
}

export default FinancialReportWidget;